import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';

// Helper function to extract error message
const getErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  if (error?.message) return error.message;
  if (error?.data?.message) return error.data.message;
  if (error?.response?.data?.message) return error.response.data.message;
  return 'An error occurred';
};

// Async Thunks
export const fetchPhoneNumbers = createAsyncThunk(
  'phoneNumbers/fetchAll',
  async ({ wabaId, page = 1, limit = 20, status, verificationStatus, sort = '-createdAt' }, { rejectWithValue }) => {
    try {
      const params = { page, limit, sort };
      if (status) params.status = status;
      if (verificationStatus) params.verificationStatus = verificationStatus;
      
      const response = await apiService.phoneNumbers.getByWaba(wabaId, params);
      // Return whole response which has { data, pagination }
      return response.data;
    } catch (error) {
      const errorMsg = getErrorMessage(error.response?.data || error);
      return rejectWithValue(errorMsg);
    }
  }
);

export const createPhoneNumber = createAsyncThunk(
  'phoneNumbers/create',
  async (phoneNumberData, { rejectWithValue }) => {
    try {
      const response = await apiService.phoneNumbers.create(phoneNumberData);
      return response.data;
    } catch (error) {
      const errorMsg = getErrorMessage(error.response?.data || error);
      return rejectWithValue(errorMsg);
    }
  }
);

export const phoneNumberRegister = createAsyncThunk(
  'phoneNumbers/phoneNumberRegister',
  async (phoneNumberData, { rejectWithValue }) => {
    try {
      const phoneNumberId = phoneNumberData?.phoneNumberId || phoneNumberData?.phone_number_id || phoneNumberData?.id || phoneNumberData?.phoneNumber;
      const pin = phoneNumberData?.pin;

      if (!phoneNumberId) {
        return rejectWithValue('Phone number ID is missing.');
      }

      if (!pin || !/^\d{6}$/.test(String(pin))) {
        return rejectWithValue('A valid 6-digit PIN is required.');
      }

      const response = await apiService.phoneNumbers.phoneNumberRegister({ phoneNumberId, pin });
      return response.data;
    } catch (error) {
      const errorMsg = getErrorMessage(error.response?.data || error);
      return rejectWithValue(errorMsg);
    }
  }
);

export const fetchPhoneNumberById = createAsyncThunk(
  'phoneNumbers/fetchById',
  async (phoneNumberId, { rejectWithValue }) => {
    try {
      const response = await apiService.phoneNumbers.getById(phoneNumberId);
      return response.data;
    } catch (error) {
      const errorMsg = getErrorMessage(error.response?.data || error);
      return rejectWithValue(errorMsg);
    }
  }
);

export const syncPhoneNumbers = createAsyncThunk(
  'phoneNumbers/sync',
  async (wabaId, { rejectWithValue }) => {
    try {
      const response = await apiService.phoneNumbers.sync();
      return response.data;
    } catch (error) {
      const errorMsg = getErrorMessage(error.response?.data || error);
      return rejectWithValue(errorMsg);
    }
  }
);

export const setDefaultPhoneNumber = createAsyncThunk(
  'phoneNumbers/setDefault',
  async ({ phoneNumberId, wabaId }, { rejectWithValue }) => {
    try {
      const response = await apiService.phoneNumbers.setDefault(phoneNumberId, { wabaId });
      return response.data;
    } catch (error) {
      const errorMsg = getErrorMessage(error.response?.data || error);
      return rejectWithValue(errorMsg);
    }
  }
);

export const updatePhoneNumberStatus = createAsyncThunk(
  'phoneNumbers/updateStatus',
  async ({ phoneNumberId, status, isPrimary }, { rejectWithValue }) => {
    try {
      const response = await apiService.phoneNumbers.updateStatus(phoneNumberId, { status, isPrimary });
      return response.data;
    } catch (error) {
      const errorMsg = getErrorMessage(error.response?.data || error);
      return rejectWithValue(errorMsg);
    }
  }
);

export const getPhoneNumberStats = createAsyncThunk(
  'phoneNumbers/getStats',
  async (wabaId, { rejectWithValue }) => {
    try {
      const response = await apiService.phoneNumbers.getStats(wabaId);
      return response.data;
    } catch (error) {
      const errorMsg = getErrorMessage(error.response?.data || error);
      return rejectWithValue(errorMsg);
    }
  }
);

export const getPhoneNumberMetrics = createAsyncThunk(
  'phoneNumbers/getMetrics',
  async (wabaId, { rejectWithValue }) => {
    try {
      const response = await apiService.phoneNumbers.getMetrics(wabaId);
      return response.data;
    } catch (error) {
      const errorMsg = getErrorMessage(error.response?.data || error);
      return rejectWithValue(errorMsg);
    }
  }
);

export const getDefaultPhoneNumber = createAsyncThunk(
  'phoneNumbers/getDefault',
  async (wabaId, { rejectWithValue }) => {
    try {
      const response = await apiService.phoneNumbers.getDefault(wabaId);
      return response.data;
    } catch (error) {
      const errorMsg = getErrorMessage(error.response?.data || error);
      return rejectWithValue(errorMsg);
    }
  }
);

export const deletePhoneNumber = createAsyncThunk(
  'phoneNumbers/delete',
  async (phoneNumberId, { rejectWithValue }) => {
    try {
      await apiService.phoneNumbers.delete(phoneNumberId);
      return phoneNumberId;
    } catch (error) {
      const errorMsg = getErrorMessage(error.response?.data || error);
      return rejectWithValue(errorMsg);
    }
  }
);

// Initial State
const initialState = {
  items: [],
  selectedPhoneNumber: null,
  defaultPhoneNumber: null,
  stats: null,
  metrics: [],
  pagination: {
    page: 1,
    limit: 20,
    total: 0
  },
  filters: {
    status: null,
    verificationStatus: null,
    sort: '-createdAt'
  },
  loading: false,
  syncing: false,
  error: null,
  success: null
};

// Slice
const phoneNumbersSlice = createSlice({
  name: 'phoneNumbers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    selectPhoneNumber: (state, action) => {
      state.selectedPhoneNumber = action.payload;
    },
    clearSelection: (state) => {
      state.selectedPhoneNumber = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch Phone Numbers
    builder
      .addCase(fetchPhoneNumbers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPhoneNumbers.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data || [];
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchPhoneNumbers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create Phone Number
    builder
      .addCase(createPhoneNumber.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPhoneNumber.fulfilled, (state, action) => {
        state.loading = false;
        // Add new phone number to items list
        const newPhoneNumber = action.payload;
        if (newPhoneNumber && newPhoneNumber._id) {
          state.items.push(newPhoneNumber);
          state.success = 'Phone number created successfully';
        }
      })
      .addCase(createPhoneNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch Phone Number by ID
    builder
      .addCase(fetchPhoneNumberById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPhoneNumberById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedPhoneNumber = action.payload;
      })
      .addCase(fetchPhoneNumberById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Sync Phone Numbers
    builder
      .addCase(syncPhoneNumbers.pending, (state) => {
        state.syncing = true;
        state.error = null;
      })
      .addCase(syncPhoneNumbers.fulfilled, (state, action) => {
        state.syncing = false;
        state.items = action.payload.phones || [];
        state.success = `Synced ${action.payload.syncedCount || 0} phone numbers`;
      })
      .addCase(syncPhoneNumbers.rejected, (state, action) => {
        state.syncing = false;
        state.error = action.payload;
      });

    // Set Default Phone Number
    builder
      .addCase(setDefaultPhoneNumber.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setDefaultPhoneNumber.fulfilled, (state, action) => {
        state.loading = false;
        state.defaultPhoneNumber = action.payload;
        // Update items list
        state.items = state.items.map(item => ({
          ...item,
          isDefault: item._id === action.payload._id
        }));
        state.success = 'Default phone number set successfully';
      })
      .addCase(setDefaultPhoneNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update Phone Number Status
    builder
      .addCase(updatePhoneNumberStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePhoneNumberStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        if (state.selectedPhoneNumber?._id === action.payload._id) {
          state.selectedPhoneNumber = action.payload;
        }
        state.success = 'Phone number status updated';
      })
      .addCase(updatePhoneNumberStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Get Phone Number Stats
    builder
      .addCase(getPhoneNumberStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPhoneNumberStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(getPhoneNumberStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Get Phone Number Metrics
    builder
      .addCase(getPhoneNumberMetrics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPhoneNumberMetrics.fulfilled, (state, action) => {
        state.loading = false;
        state.metrics = action.payload;
      })
      .addCase(getPhoneNumberMetrics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Get Default Phone Number
    builder
      .addCase(getDefaultPhoneNumber.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDefaultPhoneNumber.fulfilled, (state, action) => {
        state.loading = false;
        state.defaultPhoneNumber = action.payload;
      })
      .addCase(getDefaultPhoneNumber.rejected, (state) => {
        state.loading = false;
        state.defaultPhoneNumber = null;
      });

    // Delete Phone Number
    builder
      .addCase(deletePhoneNumber.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePhoneNumber.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(item => item._id !== action.payload);
        state.success = 'Phone number deleted successfully';
      })
      .addCase(deletePhoneNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const {
  clearError,
  clearSuccess,
  setFilters,
  setPagination,
  selectPhoneNumber,
  clearSelection
} = phoneNumbersSlice.actions;

export default phoneNumbersSlice.reducer;
